import { z } from 'zod';
import { INITIATIVE_STATUSES } from './initiatives';
import { TREND_DIRECTIONS } from './kpi-history';
import { TRAFFIC_LIGHTS } from './status';

/**
 * Dashboard executivo (T-141, RF-34; DEC-055): a saúde da estratégia no período — empresa e perspectivas com a
 * tendência, previsto x realizado estratégico do ano, tendência geral e os blocos de exceção e de desempenho.
 */

/** Itens de cada bloco (os mais graves ou os melhores/piores). */
export const EXECUTIVE_BLOCK_LIMIT = 5;

/** Período: ano e mês juntos, ou nenhum (o último mês com dado). */
export const executiveDashboardQuerySchema = z
  .object({
    year: z.coerce.number().int().min(2000).max(2100).optional(),
    month: z.coerce.number().int().min(1).max(12).optional(),
  })
  .refine((v) => (v.year === undefined) === (v.month === undefined), {
    message: 'Informe o ano e o mês juntos.',
    path: ['month'],
  });

const light = z.enum(TRAFFIC_LIGHTS);
const block = <T extends z.ZodTypeAny>(item: T) =>
  z.object({ total: z.number().int(), items: z.array(item) }).nullable();
const kpiItem = z.object({
  kpiId: z.string().uuid(),
  code: z.string(),
  name: z.string(),
  performance: z.number().nullable(),
  light,
});

export const executiveDashboardSchema = z.object({
  project: z.object({
    id: z.string().uuid(),
    name: z.string(),
    horizonStart: z.number().int(),
    horizonEnd: z.number().int(),
  }),
  year: z.number().int(),
  month: z.number().int(),
  computedAt: z.string().nullable(),
  /** Performance da empresa no mês e no acumulado do ano; tendência em p.p. para o mês anterior. */
  company: z
    .object({
      performance: z.number().nullable(),
      light,
      ytdPerformance: z.number().nullable(),
      ytdLight: light,
      trend: z.number().nullable(),
    })
    .nullable(),
  perspectives: z.array(
    z.object({
      id: z.string().uuid(),
      name: z.string(),
      performance: z.number().nullable(),
      light,
      trend: z.number().nullable(),
    }),
  ),
  /** Previsto x realizado estratégico: os 12 meses do ano (realizado do mês e acumulado contra 100%). */
  plannedVsActual: z.object({
    bands: z.object({ greenMin: z.number(), yellowMin: z.number() }),
    months: z.array(
      z.object({
        month: z.number().int(),
        performance: z.number().nullable(),
        light,
        ytdPerformance: z.number().nullable(),
      }),
    ),
  }),
  /** Tendência geral da performance da empresa nos últimos meses com dado até o período. */
  generalTrend: z
    .object({
      direction: z.enum(TREND_DIRECTIONS),
      months: z.number().int(),
      first: z.object({ year: z.number().int(), month: z.number().int(), performance: z.number() }),
      last: z.object({ year: z.number().int(), month: z.number().int(), performance: z.number() }),
    })
    .nullable(),
  criticalObjectives: block(
    z.object({
      id: z.string().uuid(),
      name: z.string(),
      perspective: z.string(),
      performance: z.number().nullable(),
      light,
    }),
  ),
  criticalKpis: block(kpiItem),
  lateActions: block(
    z.object({
      id: z.string().uuid(),
      title: z.string(),
      dueDate: z.string(),
      planId: z.string().uuid(),
      planName: z.string(),
      responsibleName: z.string().nullable(),
    }),
  ),
  initiativesAtRisk: block(
    z.object({
      id: z.string().uuid(),
      name: z.string(),
      status: z.enum(INITIATIVE_STATUSES),
      endDate: z.string().nullable(),
      /** Fim previsto já passou. */
      late: z.boolean(),
      objective: z.string(),
    }),
  ),
  /** Os 5 melhores e os 5 piores KPIs pela performance do mês (só os com performance). */
  bestKpis: block(kpiItem),
  worstKpis: block(kpiItem),
});
export type ExecutiveDashboard = z.infer<typeof executiveDashboardSchema>;
